import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Loader2 } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MAX_PDF_MB } from "@/components/proposals/UploadDocumentDialog";
import type { AgreementRecord } from "@/types/agreement";

/**
 * Starts an agreement from the customer's own PDF. The PDF is stored for the deal first, then the agreement is made from it;
 * everyone then signs that exact file, and its signature certificate is kept beside it.
 */
export function UploadAgreementDialog({ open, onOpenChange, dealId, dealName }: { open: boolean; onOpenChange: (open: boolean) => void; dealId?: string; dealName?: string }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [deal, setDeal] = useState(dealId || "");
  const [dealLabel, setDealLabel] = useState(dealName || "");
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeal(dealId || "");
    setDealLabel(dealName || "");
    setFile(null);
    setTitle("");
    setValue("");
    setStart("");
    setEnd("");
  }, [open, dealId, dealName]);

  const pick = (f: File | null) => {
    if (!f) return setFile(null);
    if (!/\.pdf$/i.test(f.name)) return error("PDF only", "Choose a PDF file");
    if (f.size > MAX_PDF_MB * 1024 * 1024) return error("File too large", `The PDF must be under ${MAX_PDF_MB} MB`);
    setFile(f);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return error("Pick a deal", "Choose the deal this agreement is for");
    if (!file) return error("Choose the PDF", "Select the customer's agreement PDF");
    if (start && end && end <= start) return error("Check the dates", "The agreement must end after it starts");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("deal_id", deal);
      fd.append("file", file);
      const up: any = await api.upload("/api/Upload/File", fd);
      const stored = up?.AddtionalData || up?.data || up;
      const res = await api.post("/api/Agreement/InsertUploaded", {
        deal_id: deal,
        file_url: stored?.url,
        file_name: stored?.original_name || file.name,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(value !== "" ? { contract_value: Number(value) } : {}),
        ...(start ? { effective_date: start } : {}),
        ...(end ? { end_date: end } : {}),
      });
      const rec = unwrapObject<AgreementRecord>(res);
      success("Agreement started", `${rec.display_number} is ready to review`);
      onOpenChange(false);
      navigate(`/agreements/${rec.id}`);
    } catch (err: any) {
      error("Couldn't start the agreement", err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Use the customer's own agreement</DialogTitle>
          <DialogDescription>Upload their PDF. It goes through approval, sending and signing like any agreement, and everyone signs that exact file.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!dealId && (
            <div className="space-y-1.5">
              <Label className="text-xs">Deal *</Label>
              <SearchSelect
                value={deal}
                selectedLabel={dealLabel}
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                placeholder="Search open deals"
                getLabel={(d) => `${d.name}${d.account?.name ? ` · ${d.account.name}` : ""}${d.stage?.name ? ` (${d.stage.name})` : ""}`}
                onChange={(id, row) => {
                  setDeal(id);
                  setDealLabel(row ? `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}` : "");
                }}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">PDF *</Label>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-3 text-left text-sm hover:bg-muted/50">
              <FileUp className="size-4 text-primary shrink-0" />
              <span className="truncate">{file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)` : `Choose a PDF (up to ${MAX_PDF_MB} MB)`}</span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Title</Label>
              <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="Leave empty to use the deal's name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Starts</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ends</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Contract value, GST included (₹)</Label>
              <Input type="number" min={0} step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave empty to use the accepted quotation's total" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !deal || !file}>
              {saving && <Loader2 className="size-4 animate-spin mr-2" />}
              Create agreement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
